import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startSaveAdvertiserDefaultPlatformValues } from "../../actions/platformDefaults";
import { useAdvertiserPlatformDefaults } from "./useAdvertiserPlatformDefaults";
import { SimpleAlert } from "../../components/Alerts/Alerts";
import {
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_SAVE_ERROR,
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_SAVE_SUCCESS,
} from "../../i18n/keysTranslations";
import {
  ALERT_ICON_TYPE_SUCCESS,
  FACEBOOK,
  GOOGLE,
  LINKEDIN,
  META,
  TIKTOK,
  TIKTOK_BUSINESS,
} from "../../utils/constants";
import { getPlatformDefaultsEmptyValues } from "../../utils/platformDefaults/platformDefaultsEmptyValues";

const INTEGRATIONS_KEY_BY_PLATFORM = {
  [META]: FACEBOOK,
  [TIKTOK]: TIKTOK_BUSINESS,
  [GOOGLE]: GOOGLE,
  [LINKEDIN]: LINKEDIN,
};

const isPlatformIntegrationActive = ({
  integrations,
  platform,
  integrationID,
}) => {
  if (!integrationID) {
    return false;
  }

  const integrationsKey = INTEGRATIONS_KEY_BY_PLATFORM[platform];
  const platformIntegrations = integrations?.[integrationsKey];

  if (!integrationsKey || platformIntegrations == null) {
    return true;
  }

  const integration = platformIntegrations[integrationID];

  return Boolean(integration?.isActive);
};

export const usePlatformDefaultsSettingsForm = ({ platform }) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const integrations = useSelectorApp((state) => state.user.integrations);
  const emptyValues = useMemo(
    () => getPlatformDefaultsEmptyValues({ platform }),
    [platform]
  );

  const formMethods = useForm({
    defaultValues: emptyValues,
  });

  const { platformDefaults, isLoaded } = useAdvertiserPlatformDefaults({
    platforms: platform ? [platform] : [],
    enabled: Boolean(platform),
  });
  const isLoadingDefaults = !isLoaded;
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const integrationID = formMethods.watch("integrationID");

  useEffect(() => {
    setSaveError("");
  }, [platform]);

  useEffect(() => {
    if (!isLoaded) return;

    formMethods.reset({
      ...emptyValues,
      ...(platformDefaults[platform] || {}),
    });
    // formMethods identity is not part of the load contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emptyValues, isLoaded, platform, platformDefaults]);

  useEffect(() => {
    if (isLoadingDefaults || !integrationID) {
      return;
    }

    const isStillActive = isPlatformIntegrationActive({
      integrations,
      platform,
      integrationID,
    });

    if (isStillActive) {
      return;
    }

    formMethods.reset(emptyValues);
    setSaveError("");
  }, [
    emptyValues,
    formMethods,
    integrationID,
    integrations,
    isLoadingDefaults,
    platform,
  ]);

  const onSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError("");

    const result = await dispatch(
      startSaveAdvertiserDefaultPlatformValues({
        platform,
        values: formMethods.getValues(),
      })
    );

    setIsSaving(false);

    if (result?.ok) {
      await SimpleAlert({
        title: t(CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_SAVE_SUCCESS),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      return;
    }

    setSaveError(t(CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_SAVE_ERROR));
  }, [dispatch, formMethods, platform, t]);

  return {
    formMethods,
    isLoadingDefaults,
    isSaving,
    saveError,
    onSave,
  };
};
